/**
 * The subset of a React style object these helpers produce. Declared here
 * rather than imported so this package stays free of a React dependency; the
 * shape is assignable to React's own type, and mobile never imports this file.
 */
export interface PewterStyle {
  borderRadius?: number;
  border?: string;
  background?: string;
  backgroundImage?: string;
  backgroundOrigin?: string;
  backgroundClip?: string;
  boxShadow?: string;
}

/**
 * The house card edge: a 3px ring filled with a gradient running highlight →
 * shade → highlight along the diagonal, so it reads as a lit metal edge rather
 * than a drawn border. Two corners catch the light and the middle stays dark.
 *
 * The geometry is the mobile app's `PewterFrame`
 * (apps/mobile/src/shared/ui/pewter-frame.tsx) — the 155° angle, the five
 * stops and the 3px width — which took them from the web home card.
 *
 * **The colours belong to the card, not to the ring.** The app's card sits on
 * a map, so a white-cornered ring is obvious against it. Dropped onto a pale
 * glass panel on a pale page, the same ring vanished: white corners on white,
 * and a shade of 18% navy behind a 28px blur. A ring has to be lit by the room
 * it is in, which is why the app carries a second palette for dark surfaces.
 * Pass a `tone` — or the two colours outright — so the edge is always a lit
 * version of the surface it belongs to.
 *
 * Unlike the app's version this is a style, not a wrapper. A gradient border
 * on the web needs no extra element: two background layers, one clipped to the
 * padding box and one to the border box, paint the fill and the ring on the
 * same node. That keeps the corner an even width the whole way round — which a
 * stroked border cannot do while its inner and outer radii differ — without
 * adding a div inside every panel.
 */

/** A hairline ring — the app's card frame. */
export const PEWTER_WIDTH = 3;
/**
 * A matte: a wide soft frame around the card, the way a mounted print sits
 * inside its board. The card keeps its own white; the frame is the gradient.
 */
export const PEWTER_MATTE = 5;

export interface PewterTone {
  /** The lit corners. */
  highlight: string;
  /** The quarter stops, between the light and the dark. */
  mid: string;
  /** The middle of each edge, the part in shadow. */
  shade: string;
}

export const PEWTER_TONES = {
  /**
   * A white card in a white room. Stronger than the app's numbers on purpose:
   * its card sits on a map and can afford a whisper, ours sits on a pale page
   * and a whisper is nothing at all.
   */
  light: {
    highlight: "rgba(255,255,255,1)",
    mid: "rgba(150,156,190,0.75)",
    shade: "rgba(17,14,57,0.34)",
  },
  /** A card lit by a navy room — over a photo, a map, or a dark ground. */
  dark: {
    highlight: "rgba(255,255,255,0.34)",
    mid: "rgba(168,174,224,0.18)",
    shade: "rgba(255,255,255,0.06)",
  },
  /**
   * For a wide frame. The same shape, paler: at 3px a 0.34 shade is an edge, at
   * 9px it is a dark band around the card, and the thing being copied is a
   * mounted print's board — lighter than the picture, not heavier.
   */
  matte: {
    highlight: "rgba(255,255,255,0.96)",
    mid: "rgba(190,195,225,0.45)",
    shade: "rgba(17,14,57,0.14)",
  },
  /**
   * The outermost rim of a framed card: white, with the gradient's turn still
   * in it so it is lit rather than flat. The frame's outer edge is the one
   * thing between the card and the page, and a shaded stop there draws a dark
   * line around the whole thing — the reference has a bright rim.
   */
  rim: {
    highlight: "rgba(255,255,255,1)",
    mid: "rgba(255,255,255,0.96)",
    shade: "rgba(255,255,255,0.88)",
  },
  /**
   * The app's original numbers, for a surface that genuinely sits on colour and
   * only needs the hint.
   */
  soft: {
    highlight: "rgba(255,255,255,1)",
    mid: "rgba(180,184,210,0.55)",
    shade: "rgba(17,14,57,0.18)",
  },
} as const satisfies Record<string, PewterTone>;

export type PewterToneName = keyof typeof PEWTER_TONES;

/**
 * The first colour in a background value, as [r, g, b].
 *
 * Only the first stop is read: a card's gradient runs between shades of one
 * surface, so the first is enough to know which room the ring is in.
 */
function firstColor(fill: string): [number, number, number] | null {
  const rgb = /rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/.exec(fill);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  const hex = /#([0-9a-f]{3}|[0-9a-f]{6})\b/i.exec(fill);
  if (!hex) return null;
  const h = hex[1]!;
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

/**
 * Which room the card is in, read off the card itself.
 *
 * The tone is worked out rather than configured because a ring that has to be
 * remembered is a ring that gets forgotten — and the failure is silent: it
 * still paints, it is just invisible, which is exactly how the first version
 * of this shipped. Pass a tone explicitly to overrule it.
 */
export function autoTone(fill: string): PewterToneName {
  const rgb = firstColor(fill);
  if (!rgb) return "light";
  const [r, g, b] = rgb;
  // Rec. 709 luma, enough to tell a white card from a navy one.
  const luma = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luma < 0.5 ? "dark" : "light";
}

/**
 * Ways to light the band.
 *
 * No dark stops in any of them — the band is white and the gradient is what
 * moves across it, so the darkest note is a pale grey-blue. A navy stop reads
 * as a drawn line rather than as light, which is what the earlier versions
 * were doing wrong.
 *
 * **Four of these ship** (owner, 2026-09-07, amended 2026-09-13; web and
 * mobile alike):
 *
 *   - `pewter` on a wide panel you stop and read. It is a symmetric five-stop
 *     turn, so it needs an edge long enough to travel along. Compressed into
 *     a 34px pill the middle stop wins and it reads as a flat grey border.
 *   - `topLit` on a repeating grid of small cards.
 *   - `sheen` on a control sitting alone, where one sweep is the only thing
 *     that still reads as light at that size.
 *   - `gold` ONLY on a surface that is already gold-tinted: the cream policy
 *     notes in the booking sheet. The 2026-09-07 ban was about gold fighting
 *     the gold accent doing real work elsewhere, which does not apply when the
 *     band is the note's own colour lit. On a white surface it still makes two
 *     framed things read as two products: do not put it on one.
 *
 * `iridescent` remains lab-only. It stays here so the lab switcher has five to
 * compare; do not ship it.
 */
export const EDGE_STYLES = {
  /** The house frame: lit on two opposite corners, softest through the middle. */
  pewter:
    "linear-gradient(155deg, rgba(255,255,255,1) 0%, rgba(214,218,238,0.85) 28%, rgba(196,201,226,0.7) 55%, rgba(214,218,238,0.85) 78%, rgba(255,255,255,1) 100%)",
  /** One light source above: brightest along the top, softening down. */
  topLit:
    "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(226,229,244,0.85) 45%, rgba(202,207,230,0.7) 100%)",
  /** A single sweep across the corner — a highlight travelling, not a rim. */
  sheen:
    "linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.95) 18%, rgba(218,222,240,0.8) 55%, rgba(203,208,232,0.7) 100%)",
  /** The brand accent in the edge, kept pale. */
  gold: "linear-gradient(155deg, rgba(255,255,255,1) 0%, rgba(243,222,190,0.9) 26%, rgba(233,205,164,0.75) 50%, rgba(243,222,190,0.9) 74%, rgba(255,255,255,1) 100%)",
  /** Cool to warm and back — an oil-slick sheen, very faint. */
  iridescent:
    "linear-gradient(155deg, rgba(255,255,255,1) 0%, rgba(206,224,246,0.8) 22%, rgba(224,210,242,0.78) 44%, rgba(246,226,204,0.8) 66%, rgba(255,255,255,1) 100%)",
} as const;

export type EdgeStyleName = keyof typeof EDGE_STYLES;

export function pewterGradient(tone: PewterTone): string {
  return `linear-gradient(155deg, ${tone.highlight} 0%, ${tone.mid} 28%, ${tone.shade} 55%, ${tone.mid} 78%, ${tone.highlight} 100%)`;
}

export interface PewterEdgeOptions {
  /**
   * `hairline` is the app's 3px card edge. `matte` is a wide pale frame around
   * the card, the way a mounted print sits inside its board — the two go
   * together, because a wide band of the hairline's shade is a dark bezel
   * rather than light.
   */
  variant?: "hairline" | "matte";
  /** Left out, the tone follows the variant and the card's own colour. */
  tone?: PewterToneName | PewterTone;
  /**
   * Overrides the variant's width. The inner corner follows on its own — CSS
   * rounds the padding box to `radius - width`, which is the arithmetic the
   * app's frame has to do by hand.
   */
  width?: number;
  /**
   * A layer beneath the gradient, in the border area. The gradient's stops are
   * part-transparent, so without something under them the band shows the page
   * through itself; with white under them it is a white band with the
   * gradient's light on it.
   */
  base?: string;
  /** A named light, instead of building one from `tone`. */
  style?: EdgeStyleName;
}

/**
 * @param fill    the surface's own background, as a gradient or a solid colour
 *                (a bare colour must be wrapped — see `solid` — since it sits
 *                in a background-image stack).
 * @param radius  the OUTER radius in px.
 */
export function pewterEdge(
  fill: string,
  radius: number,
  options: PewterToneName | PewterTone | PewterEdgeOptions = {},
): PewterStyle {
  const opts: PewterEdgeOptions =
    typeof options === "string" || "highlight" in options ? { tone: options } : options;
  const matte = opts.variant === "matte";
  const tone = opts.tone ?? (matte && autoTone(fill) === "light" ? "matte" : autoTone(fill));
  const colors = typeof tone === "string" ? PEWTER_TONES[tone] : tone;

  // Layers paint first on top. The card's fill covers the padding box; the
  // gradient sits in the border; `base` sits under the gradient, which is what
  // makes the band read as white with light on it rather than as whatever
  // happens to be behind the card.
  const light = opts.style ? EDGE_STYLES[opts.style] : pewterGradient(colors);
  const layers = [fill, light, ...(opts.base ? [opts.base] : [])];
  const boxes = ["padding-box", ...layers.slice(1).map(() => "border-box")];

  return {
    borderRadius: radius,
    border: `${opts.width ?? (matte ? PEWTER_MATTE : PEWTER_WIDTH)}px solid transparent`,
    backgroundImage: layers.join(", "),
    backgroundOrigin: layers.map(() => "border-box").join(", "),
    backgroundClip: boxes.join(", "),
  };
}

/**
 * The framed card: a thin ring, a gap of light, then the card inside it.
 *
 * Not the same thing as a wide `matte` ring, which is what this looked like
 * from a distance. In the reference the stroke is a hairline and the width
 * comes from *space* between it and the card — so the card has an edge of its
 * own and the two are separated, rather than the card ending where the border
 * begins. Widening the ring instead just gives one thick edge.
 *
 * Two styles because it is genuinely two surfaces. The outer holds the ring
 * and the gap, the inner is the card:
 *
 *     <div style={pewterFrame(26)}>
 *       <div style={pewterInner(WHITE, 26)}>…</div>
 *     </div>
 */
/** The band around the card: white, with the gradient painted on top of it. */
export const PEWTER_GAP = 4;

/**
 * The frame: a 4px white band with the gradient painted on top of it, and the
 * card inside.
 *
 * One band, two layers — not a stroke beside a band. White underneath gives
 * the gradient something to be light *on*; the gradient's own stops are
 * part-transparent, so on its own the band takes the colour of whatever is
 * behind the card and stops reading as white at all.
 */
/** The outer container's own hairline, drawn just outside the band. */
export const PEWTER_OUTLINE = "rgba(255,255,255,0.9)";

export function pewterFrame(
  radius: number,
  options: {
    gap?: number;
    tone?: PewterToneName | PewterTone;
    base?: string;
    /** Set to null for no outer hairline. */
    outline?: string | null;
    style?: EdgeStyleName;
  } = {},
): PewterStyle {
  const outline = options.outline === undefined ? PEWTER_OUTLINE : options.outline;
  return {
    ...pewterEdge(solid("transparent"), radius, {
      tone: options.tone ?? "light",
      style: options.style ?? "pewter",
      width: options.gap ?? PEWTER_GAP,
      base: options.base ?? solid("rgba(255,255,255,1)"),
    }),
    // A spread ring rather than a second element: it sits just outside the
    // band and takes the same corner radius on its own.
    ...(outline ? { boxShadow: `0 0 0 1px ${outline}` } : {}),
  };
}

/** The card inside a `pewterFrame`. Its radius follows the frame's, less the band. */
export function pewterInner(
  fill: string,
  frameRadius: number,
  options: { gap?: number; hairline?: string } = {},
): PewterStyle {
  const gap = options.gap ?? PEWTER_GAP;
  return {
    background: fill,
    borderRadius: Math.max(0, frameRadius - gap),
    // The card's own edge. Without it the card dissolves into the band and the
    // frame reads as one thick border again — 0.07 was too faint to do that
    // job between two whites.
    boxShadow: `0 0 0 1px ${options.hairline ?? "rgba(17,14,57,0.13)"}`,
  };
}

/** A solid colour, ready to pass as `fill`. */
export function solid(color: string): string {
  return `linear-gradient(${color}, ${color})`;
}
